import {
  Avatar,
  Box,
  Card,
  CardContent,
  Tooltip,
  Typography,
  Stack,
  Chip,
  alpha,
  useTheme,
  Button,
} from "@mui/material";
import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  People as PeopleIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { getTodaysTeam } from "../../api/services/notification";

const TodaysTeam = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery("todaysteam", getTodaysTeam, {
    enabled: true,
    keepPreviousData: true,
  });

  const todaysteam = data?.data || [];

  const handleViewAll = () => {
    navigate("/operations?tab=unallocated");
  };

  const getStatusColor = (status) => {
    const colors = {
      available: theme.palette.success.main,
      busy: theme.palette.warning.main,
      offline: theme.palette.error.main,
      on_break: theme.palette.info.main,
    };
    return colors[status?.toLowerCase()] || theme.palette.grey[500];
  };

  return (
    <Card
      sx={{
        height: 320,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, pb: 1, flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PeopleIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              Today's Team
            </Typography>
            {todaysteam.length > 0 && (
              <Chip
                label={todaysteam.length}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              />
            )}
          </Box>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            onClick={handleViewAll}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.7rem",
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            View All
          </Button>
        </Box>
      </CardContent>

      <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : todaysteam.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" align="center">
              No team members today
            </Typography>
            <Typography variant="caption" color="text.secondary" align="center">
              Check back later 👋
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 1.5,
              mt: 0.5,
            }}
          >
            {todaysteam.map((member) => (
              <Tooltip
                key={member.id}
                title={member.name}
                arrow
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={member.profile_photo}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {!member.profile_photo && member.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: getStatusColor(member.status),
                        border: `2px solid white`,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.5,
                      fontWeight: 500,
                      fontSize: "0.7rem",
                      textAlign: "center",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#1A1A2E",
                    }}
                  >
                    {member.name}
                  </Typography>
                </Box>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default TodaysTeam;